import type { Dict } from "../types";

export const admin: Dict = {
  en: {

    // AdminImport toast messages (errorToast / successToast wrapper)
    "AdminImport.toast.salonRequired": "Please select a salon first",
    "AdminImport.toast.emptyFile": "The file contains no data",
    "AdminImport.toast.invalidFormat": "Please upload a CSV file",
    "AdminImport.toast.readFileFailed": "Could not read the file",
    "AdminImport.toast.appointmentsImported": "{count} appointment(s) imported successfully",
    "AdminImport.toast.importAppointmentsFailed": "Could not import appointments",

    // AdminImport unified tab header (Task 14: clients | appointments | inventory | gift cards)
    "AdminImport.header.title": "Data import",
    "AdminImport.header.subtitle": "Import clients, appointments, products or gift cards for a salon from an Excel or CSV file.",
    "AdminImport.tab.clients": "Clients",
    "AdminImport.tab.appointments": "Appointments",
    "AdminImport.tab.inventory": "Inventory",
    "AdminImport.tab.giftCards": "Gift cards",
    "AdminImport.salon": "Salon",

    // Admin toast messages (errorToast / successToast wrapper)
    "Admin.toast.emailUpdated": "Email updated",
    "Admin.toast.enterEmail": "Please enter an email address",
    "Admin.toast.fillAllFields": "Please fill in all fields",
    "Admin.toast.passwordUpdated": "Password updated",
    "Admin.toast.searchUserFailed": "Could not search for the user",
    "Admin.toast.updateEmailFailed": "Could not update the email",
    "Admin.toast.updatePasswordFailed": "Could not update the password",
    "Admin.toast.userFound": "User found: {name}",
    "Admin.toast.userNotFound": "User not found",

    // Admin specific

    // AdminDashboard - Create Owner
    "AdminDashboard.createNewSalon": "Create New Salon",

    // AdminDashboard
    "AdminDashboard.title": "Admin Dashboard",
    "AdminDashboard.nav.overview": "Overview",
    "AdminDashboard.nav.createOwner": "Create owner",
    "AdminDashboard.nav.createClient": "Create client",
    // Merged-module nav
    "AdminDashboard.nav.account": "Create account",
    "AdminDashboard.nav.manageUser": "Users",
    "AdminDashboard.nav.updateEmail": "Email",
    "AdminDashboard.nav.updatePassword": "Password",
    "AdminDashboard.nav.twoFactor": "Security",
    "AdminDashboard.nav.import": "Import",
    "AdminDashboard.nav.salons": "Salons",
    "AdminDashboard.nav.aiManagement": "AI",
    "AdminDashboard.nav.bookingAnalytics": "Journey",
    "AdminDashboard.nav.onboarding": "Onboarding",
    "AdminDashboard.nav.roadmap": "Product Roadmap",
    "AdminDashboard.nav.seatLimits": "Seats and locations",
    "AdminDashboard.seatLimits.title": "Seats and locations",
    "AdminDashboard.seatLimits.intro": "Set each brand's owner and employee seat caps and, for an Enterprise brand, its number of locations. Leave a field empty to follow the plan. Lowering a seat cap never removes anyone, it only prevents adding more. A location cap can only go above the plan.",
    "AdminDashboard.seatLimits.owners": "Owners",
    "AdminDashboard.seatLimits.employees": "Employees",
    "AdminDashboard.seatLimits.followPlan": "Follow plan",
    "AdminDashboard.seatLimits.save": "Save",
    "AdminDashboard.seatLimits.saving": "Saving",
    "AdminDashboard.seatLimits.confirm": "Save anyway",
    "AdminDashboard.seatLimits.cancel": "Cancel",
    "AdminDashboard.seatLimits.freezeWarning": "This cap is below what the brand uses today. Nobody is removed, but they will not be able to add or reactivate anyone on that side until they drop below the cap.",
    "AdminDashboard.seatLimits.badge.staleTier": "Set while the brand was on {tier}",
    "AdminDashboard.seatLimits.badge.belowPlan": "Cap below the plan",
    "AdminDashboard.seatLimits.badge.frozen": "Frozen, {count} too many",
    "AdminDashboard.seatLimits.locations": "Locations",
    "AdminDashboard.seatLimits.badge.locationsInactive": "Location upgrade inactive, the brand is no longer Enterprise",
    "AdminDashboard.seatLimits.notePlaceholder": "Why this change (optional)",
    "AdminDashboard.seatLimits.history": "History",
    "AdminDashboard.seatLimits.historyEmpty": "No change recorded yet",
    "AdminDashboard.seatLimits.loadError": "Could not load seat limits.",
    "AdminDashboard.seatLimits.toast.saved": "Seat caps saved for {salon}.",
    "AdminDashboard.seatLimits.toast.saveFailed": "Could not save the seat caps.",
    "AdminDashboard.booking.title": "Customer journey",
    "AdminDashboard.booking.subtitle": "Where visitors drop off on the booking page, and who comes back",
    "AdminDashboard.booking.tabs.funnel": "Funnel",
    "AdminDashboard.booking.tabs.visitors": "Visitors",
    "AdminDashboard.booking.allSalons": "All salons",
    "AdminDashboard.booking.window.7": "7 days",
    "AdminDashboard.booking.window.30": "30 days",
    "AdminDashboard.booking.window.90": "90 days",
    "AdminDashboard.booking.includeExcluded": "Include test sessions",
    "AdminDashboard.booking.includeExcludedHint": "Salons testing their own link, bots, blacklisted clients",
    "AdminDashboard.booking.kpi.sessions": "Sessions",
    "AdminDashboard.booking.kpi.visitors": "Unique visitors",
    "AdminDashboard.booking.kpi.conversion": "Conversion rate",
    "AdminDashboard.booking.kpi.authWallDropoff": "Auth wall drop-off",
    "AdminDashboard.booking.kpi.noSlots": "Saw no slots",
    "AdminDashboard.booking.step.sessions": "Arrived on the page",
    "AdminDashboard.booking.step.location": "Picked a location",
    "AdminDashboard.booking.step.service": "Reached the Service step",
    "AdminDashboard.booking.step.hairdresser": "Reached the Professional step",
    "AdminDashboard.booking.step.datetime": "Reached the Date & time step",
    "AdminDashboard.booking.step.authWall": "Hit the account wall",
    "AdminDashboard.booking.step.signup": "Created an account",
    "AdminDashboard.booking.step.review": "Signed in (reached summary)",
    "AdminDashboard.booking.step.converted": "Booked",
    "AdminDashboard.booking.authBreakdown.title": "How they crossed the account wall",
    "AdminDashboard.booking.authBreakdown.signup": "New email account",
    "AdminDashboard.booking.authBreakdown.oauth": "Google",
    "AdminDashboard.booking.authBreakdown.already": "Already signed in",
    "AdminDashboard.booking.authBreakdown.other": "Other",
    "AdminDashboard.booking.signupFailed": "{n} failed to create an account at the wall",
    "AdminDashboard.booking.rank.location": "Location",
    "AdminDashboard.booking.rank.service": "Service",
    "AdminDashboard.booking.rank.hairdresser": "Professional",
    "AdminDashboard.booking.rank.datetime": "Slot",
    "AdminDashboard.booking.rank.review": "Summary",
    "AdminDashboard.booking.rank.booked": "Booked",
    "AdminDashboard.booking.biggestDrop": "Biggest drop",
    "AdminDashboard.booking.locationSkipped": "This salon has a single location: the location step is skipped, so it is not a drop-off.",
    "AdminDashboard.booking.excludedNote": "{count} parasite session(s) excluded",
    "AdminDashboard.booking.pickSalon": "Pick a salon to see its visitors.",
    "AdminDashboard.booking.visitor": "Visitor",
    "AdminDashboard.booking.visits": "Visits",
    "AdminDashboard.booking.lastSeen": "Last seen",
    "AdminDashboard.booking.furthestStep": "Furthest step",
    "AdminDashboard.booking.booked": "Booked",
    "AdminDashboard.booking.anonymous": "Anonymous",
    "AdminDashboard.booking.empty": "No data for this window yet.",
    "AdminDashboard.onboarding.title": "New clients & onboarding",
    "AdminDashboard.onboarding.subtitle": "Where each signed-up salon stands in its setup",
    "AdminDashboard.onboarding.tabs.inProgress": "In progress",
    "AdminDashboard.onboarding.tabs.done": "Completed",
    "AdminDashboard.onboarding.col.salon": "Salon / Owner",
    "AdminDashboard.onboarding.col.notifications": "Notifications",
    "AdminDashboard.onboarding.pause.on": "Notifications active",
    "AdminDashboard.onboarding.pause.sinceToday": "Paused since today",
    "AdminDashboard.onboarding.pause.sinceOneDay": "Paused for 1 day",
    "AdminDashboard.onboarding.pause.sinceDays": "Paused for {days} days",
    "AdminDashboard.onboarding.pause.aria": "Toggle client notifications for this salon",
    "AdminDashboard.onboarding.pause.banner": "{count} salon(s) with notifications paused",
    "AdminDashboard.onboarding.pause.confirmTitle": "Pause this salon's client notifications?",
    "AdminDashboard.onboarding.pause.confirmBody": "While notifications are paused, appointments entered by the salon trigger no confirmation, change or cancellation notice. Bookings made by clients online or through the voice agent are notified as usual, and reminders are sent for everyone. Only pause during a migration, and turn it back on right after.",
    "AdminDashboard.onboarding.pause.confirmAction": "Pause notifications",
    "AdminDashboard.onboarding.col.plan": "Plan",
    "AdminDashboard.onboarding.col.signedUp": "Signed up",
    "AdminDashboard.onboarding.col.progress": "Progress",
    "AdminDashboard.onboarding.milestone.install1": "Install 1",
    "AdminDashboard.onboarding.milestone.import": "Import",
    "AdminDashboard.onboarding.milestone.install2": "Install 2",
    "AdminDashboard.onboarding.milestone.qr": "Q&A",
    "AdminDashboard.onboarding.milestone.ai": "AI agent",
    "AdminDashboard.onboarding.kpi.inProgress": "In progress",
    "AdminDashboard.onboarding.kpi.signedUpWeek": "Signed up this week",
    "AdminDashboard.onboarding.kpi.waiting": "Waiting (no milestone)",
    "AdminDashboard.onboarding.kpi.completed": "Onboarding completed",
    "AdminDashboard.onboarding.notApplicable": "Not applicable",
    "AdminDashboard.onboarding.empty": "No salon here yet.",
    "AdminDashboard.onboarding.toast.milestoneFailed": "Could not save this milestone. Try again.",
    "AdminDashboard.onboarding.toast.notificationsPauseFailed": "Could not change the notification setting. Try again.",
    "AdminDashboard.onboarding.toast.notificationsPaused": "Notifications paused for this salon.",
    "AdminDashboard.onboarding.toast.notificationsResumed": "Notifications turned back on for this salon.",

    "AdminDashboard.subtitle": "Platform performance overview",
    "AdminDashboard.signOut": "Sign Out",
    "AdminDashboard.day": "Day",
    "AdminDashboard.week": "Week",
    "AdminDashboard.month": "Month",
    "AdminDashboard.allTime": "All Time",
    "AdminDashboard.generalPerformance": "General Performance",
    "AdminDashboard.aiReceptionist": "AI Receptionist",
    "AdminDashboard.salonInsights": "Salon Insights",
    "AdminDashboard.createOwner": "Create Owner",
    "AdminDashboard.totalSalons": "Total Salons",
    "AdminDashboard.totalCustomers": "Total Customers",
    "AdminDashboard.totalOwners": "Total Owners",
    "AdminDashboard.totalAdmins": "Total Admins",
    "AdminDashboard.newSalons": "New Salons",
    "AdminDashboard.totalAppointments": "Total Appointments",
    "AdminDashboard.totalAICalls": "Total AI Calls",
    "AdminDashboard.allTimeLabel": "All time",
    "AdminDashboard.bookingSuccessRate": "Booking Success Rate",
    "AdminDashboard.callsTransferred": "Calls Transferred",
    "AdminDashboard.toHumanAgents": "To human agents",
    "AdminDashboard.topCallReasons": "Top Call Reasons",
    "AdminDashboard.noCallData": "No call data available",
    "AdminDashboard.avgBookingsPerSalon": "Avg Bookings/Salon",
    "AdminDashboard.avgAppointmentValue": "Avg Appointment Value",
    "AdminDashboard.mostActiveSalons": "Most Active Salons",
    "AdminDashboard.topStylists": "Top Employees",
    "AdminDashboard.popularServices": "Popular Services",
    "AdminDashboard.noData": "No data available",
    "AdminDashboard.bookings": "bookings",
    "AdminDashboard.appointments": "appointments",
    "AdminDashboard.createOwnerTitle": "Create New Owner Account",
    "AdminDashboard.createOwnerDesc": "Create a new owner account and assign them to a salon",
    "AdminDashboard.fullName": "Full Name",
    "AdminDashboard.fullNamePlaceholder": "John Doe",
    "AdminDashboard.email": "Email",
    "AdminDashboard.emailPlaceholder": "owner@example.com",
    "AdminDashboard.clientEmailPlaceholder": "client@example.com",
    "AdminDashboard.password": "Password",
    "AdminDashboard.passwordPlaceholder": "••••••••",
    "AdminDashboard.phone": "Phone",
    "AdminDashboard.phonePlaceholder": "+1234567890",
    "AdminDashboard.phoneInvalid": "The phone number is invalid",
    "AdminDashboard.assignToSalon": "Assign to Salon",
    "AdminDashboard.salon": "Salon",
    "AdminDashboard.selectSalon": "Select a salon...",
    "AdminDashboard.createAccount": "Create Account",
    "AdminDashboard.creating": "Creating...",
    "AdminDashboard.successCreated": "Success!",
    "AdminDashboard.ownerCreatedDesc": "Owner account created successfully.",
    "AdminDashboard.ownerAndSalonCreatedDesc": "Owner account and new salon created successfully.",
    "AdminDashboard.errorCreating": "Error",
    "AdminDashboard.failedToCreate": "Failed to create owner account. Please try again.",
    "AdminDashboard.ownerCreated": "Owner created",
    // Account module (owner + client)
    "AdminDashboard.account.title": "Create an account",
    "AdminDashboard.account.subtitle": "Create an owner or client account and assign it to a salon.",
    "AdminDashboard.account.tabOwner": "Owner",
    "AdminDashboard.account.tabClient": "Client",
    // Manage-user module (email + password)
    "AdminDashboard.manageUser.title": "Manage a user",
    "AdminDashboard.manageUser.subtitle": "Search a user by email, then change their email or password.",
    "AdminDashboard.manageUser.tabEmail": "Email",
    "AdminDashboard.manageUser.tabPassword": "Password",
    "AdminDashboard.manageUser.currentEmail": "Current email",
    "AdminDashboard.manageUser.currentEmailPlaceholder": "user@example.com",
    "AdminDashboard.manageUser.search": "Search",
    "AdminDashboard.manageUser.searching": "...",
    "AdminDashboard.manageUser.foundUser": "User found",
    "AdminDashboard.manageUser.newEmail": "New email",
    "AdminDashboard.manageUser.newEmailPlaceholder": "new@example.com",
    "AdminDashboard.manageUser.newPassword": "New password",
    "AdminDashboard.manageUser.newPasswordPlaceholder": "Minimum 10 characters",
    "AdminDashboard.manageUser.passwordHint": "Min. 10 characters, with an uppercase letter, a lowercase letter, a number and a special character",
    "AdminDashboard.manageUser.updateEmail": "Update email",
    "AdminDashboard.manageUser.updatePassword": "Update password",
    "AdminDashboard.manageUser.updating": "Updating...",
    // Salons maintenance module
    "AdminDashboard.salons.title": "Salon maintenance",
    "AdminDashboard.salons.subtitle": "Export then permanently delete cancelled salons, and purge orphan accounts.",
    // AI module subtitle
    "AdminDashboard.ai.subtitle": "Monitor the AI receptionist activity and publish the prompt to all salons.",
    "AdminDashboard.createClientTitle": "Create New Client Account",
    "AdminDashboard.createClientDesc": "Create a new client and assign them to a salon, with or without a login account.",
    "AdminDashboard.clientModeLabel": "Account type",
    "AdminDashboard.clientModeLogin": "Login account",
    "AdminDashboard.clientModeLoginDesc": "Email + password. The client can sign in and book.",
    "AdminDashboard.clientModeFile": "Profile only (no login)",
    "AdminDashboard.clientModeFileDesc": "A client file attached to the salon, without sign-in.",
    "AdminDashboard.enrichedSection": "Additional details (optional)",
    "AdminDashboard.optional": "optional",
    "AdminDashboard.cancel": "Cancel",
    "AdminDashboard.createAnyway": "Create anyway",
    "AdminDashboard.clientCreated": "Client account created",
    "AdminDashboard.clientFileCreated": "Client profile created",
    "AdminDashboard.clientFailedToCreate": "Failed to create client. Please try again.",
    "AdminDashboard.duplicatePhoneTitle": "Duplicate phone number",
    "AdminDashboard.duplicatePhoneOne": "A client named {name} already uses this phone number in this salon. Create anyway?",
    "AdminDashboard.duplicatePhoneMany": "{count} clients already use this phone number in this salon. Create anyway?",
    "AdminDashboard.edgeFunctionError":
      "Failed to send request to the server. Please check your connection and try again.",

    // AdminDashboard - OverviewView
    "AdminDashboard.overview.title": "Platform overview",
    "AdminDashboard.overview.growth": "Growth & revenue",
    "AdminDashboard.overview.usage": "Platform activity",
    "AdminDashboard.overview.attention": "Needs attention",
    "AdminDashboard.overview.quickActions": "Quick actions",
    "AdminDashboard.overview.loading": "Loading…",
    "AdminDashboard.overview.error": "Could not load statistics",
    "AdminDashboard.overview.mrr": "Estimated MRR",
    "AdminDashboard.overview.activeSalons": "Active salons",
    "AdminDashboard.overview.paidSubs": "Paid subscriptions",
    "AdminDashboard.overview.newSalons": "New salons (30d)",
    "AdminDashboard.overview.tierMix": "Plan breakdown",
    "AdminDashboard.overview.trials": "Active trials",
    "AdminDashboard.overview.cancellations": "Cancellations (30d)",
    "AdminDashboard.overview.appointments": "Appointments (30d)",
    "AdminDashboard.overview.revenue": "Revenue processed (30d)",
    "AdminDashboard.overview.aiMinutes": "AI voice minutes (30d)",
    "AdminDashboard.overview.sms": "SMS sent (month)",
    "AdminDashboard.overview.cancelledPending": "Cancelled salons pending",
    "AdminDashboard.overview.orphans": "Orphan accounts",
    "AdminDashboard.overview.paymentFailures": "Payment failures",
    "AdminDashboard.overview.inactiveSalons": "Inactive salons (0 appts)",
    "AdminDashboard.overview.deletionRequests": "Deletion requests (Law 25)",
    "AdminDashboard.overview.qaCreateOwner": "Create an owner",
    "AdminDashboard.overview.qaCancelled": "Manage cancelled salons",
    "AdminDashboard.overview.qaImport": "Import data",

    // AdminDashboard - AI Management view
    "AdminDashboard.ai.title": "AI Supervision",
    "AdminDashboard.ai.sectionLabel": "AI Receptionist salons",
    "AdminDashboard.ai.loading": "Loading…",
    "AdminDashboard.ai.error": "Could not load AI data",
    "AdminDashboard.ai.empty": "No AI salons found",
    "AdminDashboard.ai.colName": "Salon",
    "AdminDashboard.ai.colStatus": "Status",
    "AdminDashboard.ai.colPhone": "Phone",
    "AdminDashboard.ai.colError": "Last error",
    "AdminDashboard.ai.colAttempts": "Attempts",
    "AdminDashboard.ai.relaunch": "Relaunch activation",
    "AdminDashboard.ai.relaunching": "Relaunching…",
    "AdminDashboard.ai.relaunchSuccess": "Relaunch triggered",
    "AdminDashboard.ai.relaunchError": "Relaunch failed",
    "AdminDashboard.ai.statusActive": "active",
    "AdminDashboard.ai.statusProvisioning": "provisioning",
    "AdminDashboard.ai.statusFailed": "failed",
    // AdminDashboard - AI supervision metrics
    "AdminDashboard.ai.colCalls": "Calls",
    "AdminDashboard.ai.colMinutes": "Minutes",
    "AdminDashboard.ai.colRdv": "Bookings",
    "AdminDashboard.ai.colSuccess": "Success",
    "AdminDashboard.ai.colLastCall": "Last call",
    "AdminDashboard.ai.avgShort": "avg",
    "AdminDashboard.ai.noCalls": "no calls ({n}d)",
    "AdminDashboard.ai.window7": "7 days",
    "AdminDashboard.ai.window30": "30 days",
    "AdminDashboard.ai.tileCalls": "Calls",
    "AdminDashboard.ai.tileMinutes": "Minutes used",
    "AdminDashboard.ai.tileRdv": "Bookings generated",
    "AdminDashboard.ai.tileActive": "Active AIs",
    // AdminDashboard - AI tabs + prompt editor
    "AdminDashboard.ai.tabs.supervision": "Supervision",
    "AdminDashboard.ai.tabs.prompt": "Prompt",
    "AdminDashboard.ai.prompt.editorLabel": "AI voice prompt",
    "AdminDashboard.ai.prompt.description": "Edit the prompt once here and publish it to every salon's AI agent at once. No more editing each agent by hand in ElevenLabs.",
    "AdminDashboard.ai.prompt.sourceMaster": "Prefilled from the ElevenLabs master agent (not yet saved in Flowcut). Publish to make it the source of truth.",
    "AdminDashboard.ai.prompt.sourceEmpty": "No prompt saved yet.",
    "AdminDashboard.ai.prompt.varsLabel": "Dynamic variables",
    "AdminDashboard.ai.prompt.varsHint": "These {{…}} variables are injected at call time. Keep them in the prompt.",
    "AdminDashboard.ai.prompt.blockingMissing": "Publish blocked: the {{main_salon_id}} variable is required (without it the AI can no longer book).",
    "AdminDashboard.ai.prompt.warningMissing": "Warning, recommended variable(s) missing:",
    "AdminDashboard.ai.prompt.noteLabel": "Version note (optional)",
    "AdminDashboard.ai.prompt.notePlaceholder": "e.g. Softer tone, clearer transfer wording",
    "AdminDashboard.ai.prompt.preview": "Preview changes",
    "AdminDashboard.ai.prompt.publish": "Publish to all agents",
    "AdminDashboard.ai.prompt.loading": "Loading prompt…",
    "AdminDashboard.ai.prompt.error": "Could not load the prompt",
    "AdminDashboard.ai.prompt.diffTitle": "Change preview",
    "AdminDashboard.ai.prompt.diffSummary": "{added} added, {removed} removed",
    "AdminDashboard.ai.prompt.noChanges": "No changes to preview.",
    "AdminDashboard.ai.prompt.confirmTitle": "Publish the prompt to all salons?",
    "AdminDashboard.ai.prompt.confirmDesc": "The new prompt will be applied to the master agent and every active salon agent. Upcoming calls will use it immediately.",
    "AdminDashboard.ai.prompt.confirmCta": "Publish",
    "AdminDashboard.ai.prompt.publishing": "Publishing…",
    "AdminDashboard.ai.prompt.publishSuccess": "Prompt published.",
    "AdminDashboard.ai.prompt.publishError": "Publish failed",
    "AdminDashboard.ai.prompt.reportTitle": "Publish result",
    "AdminDashboard.ai.prompt.reportDryRun": "Simulation mode (DRY_RUN): no real agent was modified.",
    "AdminDashboard.ai.prompt.reportMaster": "Master agent",
    "AdminDashboard.ai.prompt.reportOk": "OK",
    "AdminDashboard.ai.prompt.reportFail": "Failed",
    "AdminDashboard.ai.prompt.retryFailures": "Retry failures",
    "AdminDashboard.ai.prompt.historyTitle": "Version history",
    "AdminDashboard.ai.prompt.historyEmpty": "No published version yet.",
    "AdminDashboard.ai.prompt.load": "Load",
    "AdminDashboard.ai.prompt.loadedToast": "Version loaded into the editor. Review, then publish.",
    "AdminDashboard.ai.prompt.by": "by",

    // AdminDashboard - Import Clients
    "AdminImport.selectSalon": "Select a salon",

    // Admins/Owners

    // Admin — cancelled salons tab
    "cancelledSalons.tab": "Cancelled salons",
    "cancelledSalons.title": "Cancelled salons",
    "cancelledSalons.empty": "No cancelled salons.",
    "cancelledSalons.exportAndDelete": "Export and delete",
    "cancelledSalons.eligibleAt": "Eligible at",
    "cancelledSalons.employees": "employees",
    "cancelledSalons.confirmTitle": "Confirm permanent deletion",
    "cancelledSalons.confirmBody": "This action is irreversible. Type the salon name exactly to confirm:",
    "Admin.cancelledSalons.nameMismatch": "The name you typed does not match the salon name.",
    "Admin.cancelledSalons.identifierMismatch": "What you typed does not match the account email.",
    "cancelledSalons.deletePermanently": "Delete permanently",
    "cancelledSalons.deleted": "Salon deleted successfully.",
    "cancelledSalons.partialFailure": "Deletion completed with errors",
    "cancelledSalons.exportFailed": "Export incomplete, the deletion is blocked. Tables in error:",
    "cancelledSalons.exportUnavailable": "The backup export could not be produced, the deletion is blocked.",
    "cancelledSalons.keptAccounts": "Login accounts kept because they are still attached elsewhere:",
    "cancelledSalons.deletedClientProfiles": "Client records erased:",
    "cancelledSalons.orphansTitle": "Orphan login accounts",
    "cancelledSalons.noOrphans": "No orphan accounts found.",
    "cancelledSalons.purge": "Purge",
    "cancelledSalons.purged": "Account purged.",
    "cancelledSalons.purgeConfirmTitle": "Confirm permanent purge",
    "cancelledSalons.purgeConfirmBody": "This action is irreversible. Type the account's email exactly to confirm:",
    "cancelledSalons.purgePermanently": "Purge permanently",
  },
  fr: {

    // AdminImport toast messages (errorToast / successToast wrapper)
    "AdminImport.toast.salonRequired": "Sélectionnez d'abord un salon",
    "AdminImport.toast.emptyFile": "Le fichier ne contient pas de données",
    "AdminImport.toast.invalidFormat": "Veuillez téléverser un fichier CSV",
    "AdminImport.toast.readFileFailed": "Impossible de lire le fichier",
    "AdminImport.toast.appointmentsImported": "{count} rendez-vous importé(s) avec succès",
    "AdminImport.toast.importAppointmentsFailed": "Impossible d'importer les rendez-vous",

    // AdminImport unified tab header (Task 14 : clients | rendez-vous | inventaire | cartes-cadeaux)
    "AdminImport.header.title": "Import de données",
    "AdminImport.header.subtitle": "Importez des clients, rendez-vous, produits ou cartes-cadeaux pour un salon à partir d'un fichier Excel ou CSV.",
    "AdminImport.tab.clients": "Clients",
    "AdminImport.tab.appointments": "Rendez-vous",
    "AdminImport.tab.inventory": "Inventaire",
    "AdminImport.tab.giftCards": "Cartes-cadeaux",
    "AdminImport.salon": "Salon",

    // Admin toast messages (errorToast / successToast wrapper)
    "Admin.toast.emailUpdated": "Courriel mis à jour",
    "Admin.toast.enterEmail": "Veuillez saisir une adresse courriel",
    "Admin.toast.fillAllFields": "Veuillez remplir tous les champs",
    "Admin.toast.passwordUpdated": "Mot de passe mis à jour",
    "Admin.toast.searchUserFailed": "Impossible de rechercher l'utilisateur",
    "Admin.toast.updateEmailFailed": "Impossible de mettre à jour le courriel",
    "Admin.toast.updatePasswordFailed": "Impossible de mettre à jour le mot de passe",
    "Admin.toast.userFound": "Utilisateur trouvé : {name}",
    "Admin.toast.userNotFound": "Utilisateur introuvable",

    // Admin specific


    // AdminDashboard
    "AdminDashboard.title": "Tableau de bord administrateur",
    "AdminDashboard.nav.overview": "Aperçu",
    "AdminDashboard.nav.createOwner": "Créer prop.",
    "AdminDashboard.nav.createClient": "Créer client",
    "AdminDashboard.nav.account": "Créer un compte",
    "AdminDashboard.nav.manageUser": "Utilisateurs",
    "AdminDashboard.nav.updateEmail": "Courriel",
    "AdminDashboard.nav.updatePassword": "Mot de passe",
    "AdminDashboard.nav.twoFactor": "Sécurité",
    "AdminDashboard.nav.import": "Import",
    "AdminDashboard.nav.salons": "Salons",
    "AdminDashboard.nav.aiManagement": "IA",
    "AdminDashboard.nav.bookingAnalytics": "Parcours",
    "AdminDashboard.nav.onboarding": "Onboarding",
    "AdminDashboard.nav.roadmap": "Nouveautés",
    "AdminDashboard.nav.seatLimits": "Sièges et emplacements",
    "AdminDashboard.seatLimits.title": "Sièges et emplacements",
    "AdminDashboard.seatLimits.intro": "Fixez le plafond de propriétaires et le plafond d'employés de chaque marque et, pour une marque Enterprise, son nombre d'emplacements. Un champ vide suit le forfait. Baisser un plafond de sièges ne retire jamais personne, ça empêche seulement d'en ajouter. Un plafond d'emplacements ne peut que dépasser le forfait.",
    "AdminDashboard.seatLimits.owners": "Propriétaires",
    "AdminDashboard.seatLimits.employees": "Employés",
    "AdminDashboard.seatLimits.followPlan": "Suivre le forfait",
    "AdminDashboard.seatLimits.save": "Enregistrer",
    "AdminDashboard.seatLimits.saving": "Enregistrement",
    "AdminDashboard.seatLimits.confirm": "Enregistrer quand même",
    "AdminDashboard.seatLimits.cancel": "Annuler",
    "AdminDashboard.seatLimits.freezeWarning": "Ce plafond est sous ce que la marque utilise aujourd'hui. Personne n'est retiré, mais elle ne pourra plus ajouter ni réactiver personne de ce côté tant qu'elle n'est pas redescendue sous le plafond.",
    "AdminDashboard.seatLimits.badge.staleTier": "Posé quand la marque était en {tier}",
    "AdminDashboard.seatLimits.badge.belowPlan": "Plafond sous le forfait",
    "AdminDashboard.seatLimits.badge.frozen": "Gelée, {count} de trop",
    "AdminDashboard.seatLimits.locations": "Emplacements",
    "AdminDashboard.seatLimits.badge.locationsInactive": "Surclassement inactif, la marque n'est plus Enterprise",
    "AdminDashboard.seatLimits.notePlaceholder": "Pourquoi ce changement (facultatif)",
    "AdminDashboard.seatLimits.history": "Historique",
    "AdminDashboard.seatLimits.historyEmpty": "Aucun changement enregistré",
    "AdminDashboard.seatLimits.loadError": "Impossible de charger les plafonds de sièges.",
    "AdminDashboard.seatLimits.toast.saved": "Plafonds enregistrés pour {salon}.",
    "AdminDashboard.seatLimits.toast.saveFailed": "Impossible d'enregistrer les plafonds.",
    "AdminDashboard.booking.title": "Parcours client",
    "AdminDashboard.booking.subtitle": "Où les visiteurs décrochent sur la page de réservation, et qui revient",
    "AdminDashboard.booking.tabs.funnel": "Entonnoir",
    "AdminDashboard.booking.tabs.visitors": "Visiteurs",
    "AdminDashboard.booking.allSalons": "Tous les salons",
    "AdminDashboard.booking.window.7": "7 jours",
    "AdminDashboard.booking.window.30": "30 jours",
    "AdminDashboard.booking.window.90": "90 jours",
    "AdminDashboard.booking.includeExcluded": "Inclure les sessions de test",
    "AdminDashboard.booking.includeExcludedHint": "Salons qui testent leur propre lien, robots, clients sur liste noire",
    "AdminDashboard.booking.kpi.sessions": "Sessions",
    "AdminDashboard.booking.kpi.visitors": "Visiteurs uniques",
    "AdminDashboard.booking.kpi.conversion": "Taux de conversion",
    "AdminDashboard.booking.kpi.authWallDropoff": "Décrochage au mur d'auth",
    "AdminDashboard.booking.kpi.noSlots": "Aucun créneau affiché",
    "AdminDashboard.booking.step.sessions": "Arrivés sur la page",
    "AdminDashboard.booking.step.location": "Ont choisi un emplacement",
    "AdminDashboard.booking.step.service": "A atteint l'étape Service",
    "AdminDashboard.booking.step.hairdresser": "A atteint l'étape Employé",
    "AdminDashboard.booking.step.datetime": "A atteint l'étape Date & heure",
    "AdminDashboard.booking.step.authWall": "Ont buté sur le mur du compte",
    "AdminDashboard.booking.step.signup": "Ont créé un compte",
    "AdminDashboard.booking.step.review": "Authentifié (récapitulatif atteint)",
    "AdminDashboard.booking.step.converted": "Ont réservé",
    "AdminDashboard.booking.authBreakdown.title": "Comment ils ont franchi le mur du compte",
    "AdminDashboard.booking.authBreakdown.signup": "Nouveau compte courriel",
    "AdminDashboard.booking.authBreakdown.oauth": "Google",
    "AdminDashboard.booking.authBreakdown.already": "Déjà connecté",
    "AdminDashboard.booking.authBreakdown.other": "Autre",
    "AdminDashboard.booking.signupFailed": "{n} cas d'échec d'inscription au mur du compte",
    "AdminDashboard.booking.rank.location": "Emplacement",
    "AdminDashboard.booking.rank.service": "Service",
    "AdminDashboard.booking.rank.hairdresser": "Employé",
    "AdminDashboard.booking.rank.datetime": "Créneau",
    "AdminDashboard.booking.rank.review": "Récapitulatif",
    "AdminDashboard.booking.rank.booked": "Réservé",
    "AdminDashboard.booking.biggestDrop": "Plus grosse chute",
    "AdminDashboard.booking.locationSkipped": "Ce salon n'a qu'un emplacement : l'étape est sautée, ce n'est donc pas une chute.",
    "AdminDashboard.booking.excludedNote": "{count} session(s) parasite(s) exclue(s)",
    "AdminDashboard.booking.pickSalon": "Choisissez un salon pour voir ses visiteurs.",
    "AdminDashboard.booking.visitor": "Visiteur",
    "AdminDashboard.booking.visits": "Visites",
    "AdminDashboard.booking.lastSeen": "Dernière visite",
    "AdminDashboard.booking.furthestStep": "Étape la plus avancée",
    "AdminDashboard.booking.booked": "A réservé",
    "AdminDashboard.booking.anonymous": "Anonyme",
    "AdminDashboard.booking.empty": "Aucune donnée sur cette période pour l'instant.",
    "AdminDashboard.onboarding.title": "Nouveaux clients & onboarding",
    "AdminDashboard.onboarding.subtitle": "Où en est chaque salon inscrit dans son installation",
    "AdminDashboard.onboarding.tabs.inProgress": "En cours",
    "AdminDashboard.onboarding.tabs.done": "Terminés",
    "AdminDashboard.onboarding.col.salon": "Salon / Propriétaire",
    "AdminDashboard.onboarding.col.notifications": "Notifications",
    "AdminDashboard.onboarding.pause.on": "Notifications actives",
    "AdminDashboard.onboarding.pause.sinceToday": "En pause depuis aujourd'hui",
    "AdminDashboard.onboarding.pause.sinceOneDay": "En pause depuis 1 jour",
    "AdminDashboard.onboarding.pause.sinceDays": "En pause depuis {days} jours",
    "AdminDashboard.onboarding.pause.aria": "Activer/désactiver les notifications clients de ce salon",
    "AdminDashboard.onboarding.pause.banner": "{count} salon(s) avec notifications en pause",
    "AdminDashboard.onboarding.pause.confirmTitle": "Mettre en pause les notifications clientes de ce salon ?",
    "AdminDashboard.onboarding.pause.confirmBody": "Tant que les notifications sont en pause, les rendez-vous saisis par le salon ne déclenchent aucune confirmation ni avis de modification ou d'annulation. Les réservations faites par les clientes en ligne ou par l'agent vocal sont notifiées normalement, et les rappels partent pour tout le monde. À n'utiliser que le temps d'une migration, et à réactiver aussitôt après.",
    "AdminDashboard.onboarding.pause.confirmAction": "Mettre en pause",
    "AdminDashboard.onboarding.col.plan": "Plan",
    "AdminDashboard.onboarding.col.signedUp": "Inscrit",
    "AdminDashboard.onboarding.col.progress": "Avancement",
    "AdminDashboard.onboarding.milestone.install1": "Install. 1",
    "AdminDashboard.onboarding.milestone.import": "Import.",
    "AdminDashboard.onboarding.milestone.install2": "Install. 2",
    "AdminDashboard.onboarding.milestone.qr": "Q&R",
    "AdminDashboard.onboarding.milestone.ai": "Agent IA",
    "AdminDashboard.onboarding.kpi.inProgress": "En cours d'installation",
    "AdminDashboard.onboarding.kpi.signedUpWeek": "Inscrits cette semaine",
    "AdminDashboard.onboarding.kpi.waiting": "En attente (aucun jalon)",
    "AdminDashboard.onboarding.kpi.completed": "Onboarding terminé",
    "AdminDashboard.onboarding.notApplicable": "Ne s'applique pas",
    "AdminDashboard.onboarding.empty": "Aucun salon ici pour l'instant.",
    "AdminDashboard.onboarding.toast.milestoneFailed": "Impossible d'enregistrer ce jalon. Réessayez.",
    "AdminDashboard.onboarding.toast.notificationsPauseFailed": "Impossible de modifier le réglage des notifications. Réessayez.",
    "AdminDashboard.onboarding.toast.notificationsPaused": "Notifications mises en pause pour ce salon.",
    "AdminDashboard.onboarding.toast.notificationsResumed": "Notifications réactivées pour ce salon.",

    "AdminDashboard.subtitle": "Aperçu des performances de la plateforme",
    "AdminDashboard.signOut": "Se déconnecter",
    "AdminDashboard.day": "Jour",
    "AdminDashboard.week": "Semaine",
    "AdminDashboard.month": "Mois",
    "AdminDashboard.allTime": "Tout le temps",
    "AdminDashboard.generalPerformance": "Performance générale",
    "AdminDashboard.aiReceptionist": "Réceptionniste IA",
    "AdminDashboard.salonInsights": "Aperçu des salons",
    "AdminDashboard.createOwner": "Créer un propriétaire",
    "AdminDashboard.totalSalons": "Salons totaux",
    "AdminDashboard.totalCustomers": "Clients totaux",
    "AdminDashboard.totalOwners": "Propriétaires totaux",
    "AdminDashboard.totalAdmins": "Administrateurs totaux",
    "AdminDashboard.newSalons": "Nouveaux salons",
    "AdminDashboard.totalAppointments": "Rendez-vous totaux",
    "AdminDashboard.totalAICalls": "Appels IA totaux",
    "AdminDashboard.allTimeLabel": "De tout le temps",
    "AdminDashboard.bookingSuccessRate": "Taux de réussite des réservations",
    "AdminDashboard.callsTransferred": "Appels transférés",
    "AdminDashboard.toHumanAgents": "Vers des agents humains",
    "AdminDashboard.topCallReasons": "Principales raisons d'appel",
    "AdminDashboard.noCallData": "Aucune donnée d'appel disponible",
    "AdminDashboard.avgBookingsPerSalon": "Réservations moy./Salon",
    "AdminDashboard.avgAppointmentValue": "Valeur moy. des rendez-vous",
    "AdminDashboard.mostActiveSalons": "Salons les plus actifs",
    "AdminDashboard.topStylists": "Meilleurs employés",
    "AdminDashboard.popularServices": "Services populaires",
    "AdminDashboard.noData": "Aucune donnée disponible",
    "AdminDashboard.bookings": "réservations",
    "AdminDashboard.appointments": "rendez-vous",
    "AdminDashboard.createOwnerTitle": "Créer un nouveau compte propriétaire",
    "AdminDashboard.createOwnerDesc": "Créez un nouveau compte propriétaire et assignez-le à un salon",
    "AdminDashboard.fullName": "Nom complet",
    "AdminDashboard.fullNamePlaceholder": "Jean Dupont",
    "AdminDashboard.email": "Courriel",
    "AdminDashboard.emailPlaceholder": "proprietaire@exemple.com",
    "AdminDashboard.clientEmailPlaceholder": "client@exemple.com",
    "AdminDashboard.password": "Mot de passe",
    "AdminDashboard.passwordPlaceholder": "••••••••",
    "AdminDashboard.phone": "Téléphone",
    "AdminDashboard.phonePlaceholder": "+1234567890",
    "AdminDashboard.phoneInvalid": "Le numéro de téléphone est invalide",
    "AdminDashboard.assignToSalon": "Assigner à un salon",
    "AdminDashboard.salon": "Salon",
    "AdminDashboard.selectSalon": "Sélectionner un salon...",
    "AdminDashboard.createNewSalon": "Créer un nouveau salon",
    "AdminDashboard.createAccount": "Créer un compte",
    "AdminDashboard.creating": "Création...",
    "AdminDashboard.successCreated": "Succès !",
    "AdminDashboard.ownerCreatedDesc": "Le compte propriétaire a été créé avec succès.",
    "AdminDashboard.ownerAndSalonCreatedDesc": "Le compte propriétaire et le nouveau salon ont été créés avec succès.",
    "AdminDashboard.errorCreating": "Erreur",
    "AdminDashboard.failedToCreate": "Échec de la création du compte propriétaire. Veuillez réessayer.",
    "AdminDashboard.ownerCreated": "Propriétaire créé",
    "AdminDashboard.account.title": "Créer un compte",
    "AdminDashboard.account.subtitle": "Créez un compte propriétaire ou client et assignez-le à un salon.",
    "AdminDashboard.account.tabOwner": "Propriétaire",
    "AdminDashboard.account.tabClient": "Client",
    "AdminDashboard.manageUser.title": "Modifier un utilisateur",
    "AdminDashboard.manageUser.subtitle": "Recherchez un utilisateur par courriel, puis modifiez son courriel ou son mot de passe.",
    "AdminDashboard.manageUser.tabEmail": "Courriel",
    "AdminDashboard.manageUser.tabPassword": "Mot de passe",
    "AdminDashboard.manageUser.currentEmail": "Courriel actuel",
    "AdminDashboard.manageUser.currentEmailPlaceholder": "utilisateur@example.com",
    "AdminDashboard.manageUser.search": "Chercher",
    "AdminDashboard.manageUser.searching": "...",
    "AdminDashboard.manageUser.foundUser": "Utilisateur trouvé",
    "AdminDashboard.manageUser.newEmail": "Nouveau courriel",
    "AdminDashboard.manageUser.newEmailPlaceholder": "nouveau@example.com",
    "AdminDashboard.manageUser.newPassword": "Nouveau mot de passe",
    "AdminDashboard.manageUser.newPasswordPlaceholder": "Minimum 10 caractères",
    "AdminDashboard.manageUser.passwordHint": "Min. 10 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial",
    "AdminDashboard.manageUser.updateEmail": "Mettre à jour le courriel",
    "AdminDashboard.manageUser.updatePassword": "Mettre à jour le mot de passe",
    "AdminDashboard.manageUser.updating": "Mise à jour...",
    "AdminDashboard.salons.title": "Maintenance des salons",
    "AdminDashboard.salons.subtitle": "Exportez puis supprimez définitivement les salons annulés, et purgez les comptes orphelins.",
    "AdminDashboard.ai.subtitle": "Surveillez l'activité de la réceptionniste IA et publiez le prompt vers tous les salons.",
    "AdminDashboard.createClientTitle": "Créer un nouveau compte client",
    "AdminDashboard.createClientDesc": "Créez un nouveau client et assignez-le à un salon, avec ou sans compte de connexion.",
    "AdminDashboard.clientModeLabel": "Type de compte",
    "AdminDashboard.clientModeLogin": "Compte de connexion",
    "AdminDashboard.clientModeLoginDesc": "Courriel + mot de passe. Le client peut se connecter et réserver.",
    "AdminDashboard.clientModeFile": "Fiche seulement (sans connexion)",
    "AdminDashboard.clientModeFileDesc": "Une fiche client rattachée au salon, sans connexion.",
    "AdminDashboard.enrichedSection": "Détails supplémentaires (facultatif)",
    "AdminDashboard.optional": "facultatif",
    "AdminDashboard.cancel": "Annuler",
    "AdminDashboard.createAnyway": "Créer quand même",
    "AdminDashboard.clientCreated": "Compte client créé",
    "AdminDashboard.clientFileCreated": "Fiche client créée",
    "AdminDashboard.clientFailedToCreate": "Échec de la création du client. Veuillez réessayer.",
    "AdminDashboard.duplicatePhoneTitle": "Numéro de téléphone en double",
    "AdminDashboard.duplicatePhoneOne": "Un client nommé {name} utilise déjà ce numéro dans ce salon. Créer quand même ?",
    "AdminDashboard.duplicatePhoneMany": "{count} clients utilisent déjà ce numéro dans ce salon. Créer quand même ?",
    "AdminDashboard.edgeFunctionError":
      "Échec de l'envoi de la requête au serveur. Vérifiez votre connexion et réessayez.",

    // AdminDashboard - OverviewView
    "AdminDashboard.overview.title": "Aperçu de la plateforme",
    "AdminDashboard.overview.growth": "Croissance & revenus",
    "AdminDashboard.overview.usage": "Activité de la plateforme",
    "AdminDashboard.overview.attention": "À surveiller",
    "AdminDashboard.overview.quickActions": "Actions rapides",
    "AdminDashboard.overview.loading": "Chargement…",
    "AdminDashboard.overview.error": "Impossible de charger les statistiques",
    "AdminDashboard.overview.mrr": "MRR estimé",
    "AdminDashboard.overview.activeSalons": "Salons actifs",
    "AdminDashboard.overview.paidSubs": "Abonnements payants",
    "AdminDashboard.overview.newSalons": "Nouveaux salons (30 j)",
    "AdminDashboard.overview.tierMix": "Répartition par forfait",
    "AdminDashboard.overview.trials": "Essais en cours",
    "AdminDashboard.overview.cancellations": "Annulations (30 j)",
    "AdminDashboard.overview.appointments": "RDV pris (30 j)",
    "AdminDashboard.overview.revenue": "Chiffre traité (30 j)",
    "AdminDashboard.overview.aiMinutes": "Minutes IA vocale (30 j)",
    "AdminDashboard.overview.sms": "SMS envoyés (mois)",
    "AdminDashboard.overview.cancelledPending": "Salons annulés en attente",
    "AdminDashboard.overview.orphans": "Comptes orphelins",
    "AdminDashboard.overview.paymentFailures": "Paiements en échec",
    "AdminDashboard.overview.inactiveSalons": "Salons inactifs (0 RDV)",
    "AdminDashboard.overview.deletionRequests": "Demandes de suppression (Loi 25)",
    "AdminDashboard.overview.qaCreateOwner": "Créer un propriétaire",
    "AdminDashboard.overview.qaCancelled": "Gérer les salons annulés",
    "AdminDashboard.overview.qaImport": "Importer des données",

    // AdminDashboard - Vue Gestion IA
    "AdminDashboard.ai.title": "Supervision IA",
    "AdminDashboard.ai.sectionLabel": "Salons réceptionniste IA",
    "AdminDashboard.ai.loading": "Chargement…",
    "AdminDashboard.ai.error": "Impossible de charger les données IA",
    "AdminDashboard.ai.empty": "Aucun salon IA trouvé",
    "AdminDashboard.ai.colName": "Salon",
    "AdminDashboard.ai.colStatus": "Statut",
    "AdminDashboard.ai.colPhone": "Téléphone",
    "AdminDashboard.ai.colError": "Dernière erreur",
    "AdminDashboard.ai.colAttempts": "Tentatives",
    "AdminDashboard.ai.relaunch": "Relancer l'activation",
    "AdminDashboard.ai.relaunching": "Relance…",
    "AdminDashboard.ai.relaunchSuccess": "Relance déclenchée",
    "AdminDashboard.ai.relaunchError": "Échec de la relance",
    "AdminDashboard.ai.statusActive": "actif",
    "AdminDashboard.ai.statusProvisioning": "en cours",
    "AdminDashboard.ai.statusFailed": "échec",
    // AdminDashboard - Métriques de supervision IA
    "AdminDashboard.ai.colCalls": "Appels",
    "AdminDashboard.ai.colMinutes": "Minutes",
    "AdminDashboard.ai.colRdv": "RDV",
    "AdminDashboard.ai.colSuccess": "Réussite",
    "AdminDashboard.ai.colLastCall": "Dernier appel",
    "AdminDashboard.ai.avgShort": "moy",
    "AdminDashboard.ai.noCalls": "aucun appel ({n}j)",
    "AdminDashboard.ai.window7": "7 jours",
    "AdminDashboard.ai.window30": "30 jours",
    "AdminDashboard.ai.tileCalls": "Appels",
    "AdminDashboard.ai.tileMinutes": "Minutes consommées",
    "AdminDashboard.ai.tileRdv": "RDV générés",
    "AdminDashboard.ai.tileActive": "IA actives",
    // AdminDashboard - Onglets IA + éditeur de prompt
    "AdminDashboard.ai.tabs.supervision": "Supervision",
    "AdminDashboard.ai.tabs.prompt": "Prompt",
    "AdminDashboard.ai.prompt.editorLabel": "Prompt de l'IA vocale",
    "AdminDashboard.ai.prompt.description": "Modifiez le prompt une seule fois ici et publiez-le vers l'agent IA de tous les salons d'un coup. Fini l'édition agent par agent dans ElevenLabs.",
    "AdminDashboard.ai.prompt.sourceMaster": "Prérempli depuis l'agent maître ElevenLabs (pas encore enregistré dans Flowcut). Publiez pour en faire la source de vérité.",
    "AdminDashboard.ai.prompt.sourceEmpty": "Aucun prompt enregistré pour l'instant.",
    "AdminDashboard.ai.prompt.varsLabel": "Variables dynamiques",
    "AdminDashboard.ai.prompt.varsHint": "Ces variables {{…}} sont injectées au moment de l'appel. Gardez-les dans le prompt.",
    "AdminDashboard.ai.prompt.blockingMissing": "Publication bloquée : la variable {{main_salon_id}} est obligatoire (sans elle l'IA ne peut plus réserver).",
    "AdminDashboard.ai.prompt.warningMissing": "Attention, variable(s) recommandée(s) absente(s) :",
    "AdminDashboard.ai.prompt.noteLabel": "Note de version (optionnel)",
    "AdminDashboard.ai.prompt.notePlaceholder": "ex. Ton plus doux, formulation du transfert plus claire",
    "AdminDashboard.ai.prompt.preview": "Prévisualiser les changements",
    "AdminDashboard.ai.prompt.publish": "Publier à tous les agents",
    "AdminDashboard.ai.prompt.loading": "Chargement du prompt…",
    "AdminDashboard.ai.prompt.error": "Impossible de charger le prompt",
    "AdminDashboard.ai.prompt.diffTitle": "Aperçu des changements",
    "AdminDashboard.ai.prompt.diffSummary": "{added} ajoutée(s), {removed} supprimée(s)",
    "AdminDashboard.ai.prompt.noChanges": "Aucun changement à prévisualiser.",
    "AdminDashboard.ai.prompt.confirmTitle": "Publier le prompt à tous les salons ?",
    "AdminDashboard.ai.prompt.confirmDesc": "Le nouveau prompt sera appliqué à l'agent maître et à tous les agents salons actifs. Les prochains appels l'utiliseront immédiatement.",
    "AdminDashboard.ai.prompt.confirmCta": "Publier",
    "AdminDashboard.ai.prompt.publishing": "Publication…",
    "AdminDashboard.ai.prompt.publishSuccess": "Prompt publié.",
    "AdminDashboard.ai.prompt.publishError": "Échec de la publication",
    "AdminDashboard.ai.prompt.reportTitle": "Résultat de la publication",
    "AdminDashboard.ai.prompt.reportDryRun": "Mode simulation (DRY_RUN) : aucun agent réel n'a été modifié.",
    "AdminDashboard.ai.prompt.reportMaster": "Agent maître",
    "AdminDashboard.ai.prompt.reportOk": "OK",
    "AdminDashboard.ai.prompt.reportFail": "Échec",
    "AdminDashboard.ai.prompt.retryFailures": "Relancer les échecs",
    "AdminDashboard.ai.prompt.historyTitle": "Historique des versions",
    "AdminDashboard.ai.prompt.historyEmpty": "Aucune version publiée pour l'instant.",
    "AdminDashboard.ai.prompt.load": "Charger",
    "AdminDashboard.ai.prompt.loadedToast": "Version chargée dans l'éditeur. Revoyez, puis publiez.",
    "AdminDashboard.ai.prompt.by": "par",

    // AdminDashboard - Import Clients
    "AdminImport.selectSalon": "Sélectionner un salon",

    // Admins/Propriétaires

    // Admin — onglet salons annulés
    "cancelledSalons.tab": "Salons annulés",
    "cancelledSalons.title": "Salons annulés",
    "cancelledSalons.empty": "Aucun salon annulé.",
    "cancelledSalons.exportAndDelete": "Exporter et supprimer",
    "cancelledSalons.eligibleAt": "Éligible le",
    "cancelledSalons.employees": "employés",
    "cancelledSalons.confirmTitle": "Confirmer la suppression définitive",
    "cancelledSalons.confirmBody": "Cette action est irréversible. Tapez le nom exact du salon pour confirmer :",
    "Admin.cancelledSalons.nameMismatch": "Le nom saisi ne correspond pas au nom du salon.",
    "Admin.cancelledSalons.identifierMismatch": "Ce que vous avez saisi ne correspond pas au courriel du compte.",
    "cancelledSalons.deletePermanently": "Supprimer définitivement",
    "cancelledSalons.deleted": "Salon supprimé avec succès.",
    "cancelledSalons.partialFailure": "Suppression terminée avec des erreurs",
    "cancelledSalons.exportFailed": "Export incomplet, la suppression est bloquée. Tables en échec :",
    "cancelledSalons.exportUnavailable": "La sauvegarde n'a pas pu être produite, la suppression est bloquée.",
    "cancelledSalons.keptAccounts": "Comptes de connexion conservés car encore rattachés ailleurs :",
    "cancelledSalons.deletedClientProfiles": "Fiches clients effacées :",
    "cancelledSalons.orphansTitle": "Comptes de connexion orphelins",
    "cancelledSalons.noOrphans": "Aucun compte orphelin trouvé.",
    "cancelledSalons.purge": "Purger",
    "cancelledSalons.purged": "Compte purgé.",
    "cancelledSalons.purgeConfirmTitle": "Confirmer la purge définitive",
    "cancelledSalons.purgeConfirmBody": "Cette action est irréversible. Tapez le courriel exact du compte pour confirmer :",
    "cancelledSalons.purgePermanently": "Purger définitivement",
  },
};
